import type { UpcomingVisit } from '@/api/types/ProviderApi'
import type { GlyphName } from '@/components/svgs/Glyph'
import { shouldShowCheckIn } from '@/features/visits/checkIn'
import { windowLine } from '@/features/visits/studioClock'

/** What the visit page says about where the visit stands, as a row's worth of words. */
export type VisitStateRow = { glyph: GlyphName; title: string; line: string }

/**
 * Where the visit stands, as a row rather than a pill and a footnote.
 *
 * The state used to be told twice and badly: a twelve-character capsule at the heading's right
 * margin, and a line of small print at the foot of the page saying how long before a visit
 * something opens. A row holds a title and a sentence, which is what this fact is — and it is
 * the shape this app already gives it one step later, where being checked in draws "You're In"
 * over "We'll come and get you shortly" rather than a badge.
 *
 * A row in every state the page can be in, so the page has one way of saying where things stand
 * and the heading is only ever a heading. The two live states drew no row for a while and the
 * state pill came back to cover them, which put a capsule alone at the right margin with nothing
 * to be a mark on. Where the button is live this says what the button is *for*, or how long the
 * window lasts — never the instruction the button is already giving.
 *
 * Every moment here is named rather than counted. "Confirm 4 days before your visit" is a
 * subtraction a member has to perform; "You can confirm from Fri, Sep 26" is a date they can act
 * on. The server sends both edges of both windows, so nothing in this file knows that the
 * confirmation window is four days or that the door opens fifteen minutes out.
 */
export const visitStateRow = (visit: UpcomingVisit, now: Date): VisitStateRow | null => {
	const checkIn = visit.checkIn

	// The door first, in the order `visitSlotState` tests it, so the row and the button below it
	// are always about the same thing: a member standing in a lobby having ignored every reminder
	// wants today's state, not a question from four days ago.
	if (checkIn && checkIn.status !== 'NOT_YET_OPEN') {
		if (checkIn.status === 'CHECKED_IN') {
			return {
				glyph: 'check',
				// A person's words, not a record's: "checked in" is what a system says about a row
				// in a table. This is what the receptionist would have said.
				title: "You're In",
				line: "We'll come and get you shortly",
			}
		}
		// Closed and open share the line. What changes is the title and the button under it: the
		// door is one object with three states, and the window it keeps is the same window in all
		// of them.
		return {
			glyph: 'clock',
			title: checkIn.status === 'EXPIRED' ? 'Check-in Closed' : 'Check-in Open',
			line: windowLine(visit, checkIn.opensAt, checkIn.closesAt),
		}
	}

	// Absent on a payload that predates provider-kapi#1167, and read as asking: that is how Wally
	// has always worked, so the older server's silence means the same as its yes.
	if (visit.confirmationRequired ?? true) {
		// One line for both of confirmation's unsettled states, where there were two that said
		// almost the same thing a week apart — "confirm so we keep your slot" while the studio was
		// asking, "we hold your slot until then" before it had. The difference between them is the
		// button: dark and naming the day it wakes up, or live and asking. What does not change is
		// why confirming exists, which is the thing a member is actually unsure about.
		if (visit.isConfirmable || !visit.isConfirmed) {
			return {
				glyph: 'clock',
				title: 'Not Confirmed',
				line: 'Confirming keeps your time reserved',
			}
		}
	}

	// Nothing outstanding about confirming, so the door is the next thing anyone will ask of them
	// — but only once it is close enough to be worth naming. `shouldShowCheckIn` is the hour's
	// margin the slot was given and never wired up: without it a visit confirmed in September
	// reads "Check-in opens at 12:15" for three weeks, which is a fact about a morning that has
	// not happened yet.
	if (checkIn && shouldShowCheckIn(checkIn, now)) {
		return {
			glyph: 'clock',
			title: 'Not Checked In',
			line: windowLine(visit, checkIn.opensAt, checkIn.closesAt),
		}
	}

	if (visit.isConfirmed) {
		// No date and no hour. Both are set at fifty-four points nine lines above this, and a row
		// repeating the page's own heading is the smallest type on the screen saying the largest
		// thing on it.
		return { glyph: 'check', title: 'Confirmed', line: 'See you soon' }
	}

	// Confirmation switched off and the door still hours away: nothing is outstanding and nothing
	// has been settled, so the page says neither.
	return null
}
